import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link as RouterLink } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startupName, setStartupName] = useState("");
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  useEffect(() => {
    // Get startup name from localStorage
    const storedStartupName = localStorage.getItem("STARTUP");
    if (storedStartupName) {
      setStartupName(storedStartupName);
    }
  }, []);

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Clear all authentication state
    clearAuthState();
    setIsAuthenticated(false);

    // Close the menu after logout
    setIsOpen(false);

    // Navigate to login page
    navigate("/login");
  };

  // Function to handle menu item click
  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  // Update the landing page designer link to include a query parameter.
  const menuLinks = [
    // { name: "Startup", path: "/startup" },
    // { name: "Theme Designer", path: "/theme-designer" },
    // { name: "Menu Designer", path: "/menu-builder" },
    // { name: "Site Builder", path: "/template-builder" },
    // { name: "Industry Analysis", path: `/industry-analysis/${startupName}` },

    { name: "Workflow SOW", path: "/workflow-design-summary" },
    { name: "AI Automation Designer", path: "/workflow-designer" },
    { name: "Ai Automation Processing", path: "/sales-process" },
    { name: "AI Automation Dashboard", path: "/ai-automation-dashboard" },
    { name: "API Key Registration", path: "/api-key-registration" },
    { name: "API Call Registration", path: "/api-call-registration" },
    { name: "AI Agent Designer", path: "/agent-config" },
    { name: "RAG Knowledge Base Querying", path: "/rag-query-interface" },
    { name: "RAG KB Document Submission", path: "/rag-document-submission" },
    { name: "Theme Designer", path: "/theme-designer" },
    // { name: "RAG Test Queries", path: "/rag-test-queries" },

    // { name: "Template Example", path: "/template-example" },
    // { name: "Page Designer", path: "/page-designer" },
    // { name: "Data Designer", path: "/entity-builder" },
    // { name: "Form Designer", path: "/entity-form-builder" },
    // { name: "Agent Designer", path: "/agent-config-form" },
  ];

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive
                ? "font-bold text-[145%] hover:opacity-90 focus-visible:opacity-90"
                : "text-[145%] font-bold hover:opacity-90 focus:opacity-90"
            }
            style={{ color: theme.accentColor || "#FFA72A" }}
          >
            Fipsr Agentics
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div className="md:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#FFF",
            }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex space-x-6">
          {isAuthenticated ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{
                    color: theme.accentColor || "#FFA72A",
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <NavLink
              to="/login"
              className={({ isActive }) =>
                isActive
                  ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                  : "text-[120%] hover:opacity-90 focus:opacity-90"
              }
              style={{
                color: theme.accentColor || "#FFA72A",
              }}
            >
              Login
            </NavLink>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav className="md:hidden mt-4">
          {isAuthenticated ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className="block py-2 px-4 text-[120%]"
                  style={{
                    color: theme.accentColor || "#FFA72A",
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className="block py-2 px-4 text-[120%]"
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <NavLink
              to="/login"
              onClick={handleMenuItemClick}
              className="block py-2 px-4 text-[120%]"
              style={{
                color: theme.accentColor || "#FFA72A",
              }}
            >
              Login
            </NavLink>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
